/**
 * FacilityProfileTab — data-driven facility profile form.
 *
 * On mount it calls GET /facility/{id}/profile-schema which returns a
 * render-ready payload: sections → fields → prefilled values and provenance.
 *
 * Fields are rendered dynamically by ProfileField (dispatcher on field_type).
 * No per-facility-type form logic lives in this component.
 *
 * On "Accept and Continue" the UI POSTs to /facility/{id}/profile-values with
 * approve_profile=true, then calls onComplete so the parent can switch tabs.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  PROFILE_AUTO_SAVE_DEFER_TIMEOUT_MS,
  shouldDeferProfileAutoSave,
} from "./dashboard/dashboardComplianceStreamSession";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  getFacilityProfileSchema,
  getMedicareCertPrefill,
  mapSaveFacilityProfileError,
  saveFacilityProfileValues,
  type FacilityProfileJurisdictionContext,
  type FacilityProfileSchemaResponse,
  type MedicareCertPrefillData,
  type ProfileFieldOption,
  type ProfileFieldResponse,
  type ProfileRegionResponse,
  type ProfileSectionResponse,
} from "@services/facilityManagement";
import facilityComplianceService from "@services/facilityCompliance";
import {
  augmentFieldForAnchorPresentation,
  compareProfileSectionsForDisplay,
  buildCertIdentifiersRegionSubtitle,
  accreditingSavedSelectionSlugs,
  cmsDefaultAccreditingProfileValue,
  ensureCmsAccreditingBodyOption,
  fieldVisibleForTrackedAnchors,
  filterFieldsForTrackedAnchors,
  groupFieldsIntoRows,
  isCcnProfileField,
  isDateProfileField,
  isMedicareCertificationDateField,
  resolveMedicareCertificationDateFieldKey,
  isAccreditingProfileField,
  isCmsAccreditingBodyOption,
  nonCmsAccreditorSelected,
  PROFILE_DATE_FIELD_MAX_WIDTH_PX,
  regionStyleSx,
  regionUsesTwoColumnDateGrid,
  sectionUsesAdvancedLayout,
} from "@utils/facilityProfileLayout";
import {
  ACTIVITIES_SECTION_HELPER_TEXT,
  ACTIVITIES_SECTION_KEY,
  applyLiberalActivityToggleDefaults,
} from "@utils/facilityProfileActivityDefaults";
import { applyStateLicenseRenewalDefaults } from "@utils/facilityProfileJurisdictionDefaults";
import { mergeTaxSectionForDisplay } from "@utils/title26TaxProfileUi";
import { mergeProviderBasedSectionForDisplay } from "@utils/providerBasedProfileUi";
import { validateCcnProfileValue, type CcnValidationContext } from "@utils/ccnValidation";
import { medicareCcnFromProfileValue } from "@utils/facilityUtils";
import {
  CLIA_OFF_WARNING_RHC,
  shouldWarnCliaTestingOff,
} from "@utils/cliaCertificationWarning";
import { buildMedicareManualRefreshUserSummary } from "@utils/medicareCertLookupUserSummary";
import CcnCmsLookupButton from "./CcnCmsLookupButton";
import MedicareCertLookupButton from "./MedicareCertLookupButton";
import ProfileField, {
  PROFILE_TEAL_SWITCH_SX,
  PROFILE_TOGGLE_ROW_SX,
  PROFILE_TOGGLE_TEXT_SX,
  ProfileDateCommitProvider,
  type ProfileDateCommitApi,
} from "./ProfileField";

const SHOW_TAX_COMPLIANCE_CALENDAR_FIELD_KEY = "show_tax_compliance_calendar";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface FacilityProfileTabProps {
  facilityId: number;
  /** Kept for backward-compatibility — not used to drive rendering. */
  facilityTypeSlug?: string;
  countryCode?: string;
  existingAccreditingBody?: { slug: string; name: string }[] | null;
  existingMedicareDate?: string | null;
  autoSaveOnInitialLoad?: boolean;
  onComplete: (updatedFields: Record<string, unknown>) => void;
  /**
   * Called when certain profile preferences change in-session (e.g. tax calendar visibility)
   * so tabs like Potential Penalties can react before the user clicks "Save Profile".
   */
  onPreferencePatch?: (facilityId: number, patch: Record<string, unknown>) => void;
  /**
   * When true, skip loading profile-schema until ``isProfileTabActive`` is true so the
   * Facility Analysis shell does not pay for a heavy GET while the profile tab is hidden.
   */
  deferProfileFetchUntilTabVisible?: boolean;
  /** Whether the Facility Profile tab is the active My Facility tab (used with defer). */
  isProfileTabActive?: boolean;
  /** Dashboard BFF already warmed assembled profile-schema Redis cache. */
  profileSchemaWarmedFromBff?: boolean;
  /** Hold auto-save until compliance stream init (dashboard post-checkout). */
  deferAutoSaveUntilComplianceInit?: boolean;
  /** Increment when compliance SSE emits init so deferred auto-save can run. */
  complianceStreamInitSignal?: number;
  /** Skip compliance cache invalidation on first default approve auto-save. */
  skipComplianceCacheInvalidationOnAutoSave?: boolean;
  /** When true, profile fields and save are disabled (view-only). */
  readOnly?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract the raw value from the API value_json envelope {"value": <actual>}. */
function unwrap(envelope: { value: unknown } | null | undefined): unknown {
  if (envelope == null) return null;
  if (typeof envelope === "object" && "value" in (envelope as object)) {
    return (envelope as { value: unknown }).value;
  }
  return envelope;
}

/** Build initial field-value state from schema sections. */
function buildInitialValues(
  sections: ProfileSectionResponse[]
): Record<string, unknown> {
  const vals: Record<string, unknown> = {};
  for (const section of sections) {
    for (const field of section.fields) {
      vals[field.field_key] = unwrap(field.value as { value: unknown } | null);
    }
  }
  return vals;
}

/** Returns true if the field represents the employee count selector. */
function isEmployeeCountField(field: ProfileFieldResponse): boolean {
  const key = field.field_key.toLowerCase();
  const lbl = field.label.toLowerCase();
  return (
    key.includes("employee_count") ||
    key.includes("num_employees") ||
    key.includes("employees_count") ||
    (lbl.includes("employee") && lbl.includes("count"))
  );
}

function TaxCalendarImpactTooltip() {
  return (
    <Tooltip
      arrow
      placement="right"
      title={
        <Box sx={{ p: 0.5, maxWidth: 520 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "13px", mb: 1 }}>
            Calendar impact when tax fields are not saved
          </Typography>
          <Typography sx={{ fontSize: "12px", mb: 1.25 }}>
            The Tax Compliance Calendar under Potential Penalties is hidden until you enable
            &quot;Show Tax Compliance Calendar&quot; at the top of this Tax section (saved with your
            facility profile).
          </Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 700, mb: 0.5 }}>
            Still shown by default
          </Typography>
          <Typography sx={{ fontSize: "12px", mb: 1.25 }}>
            Payroll Deposit Schedule, Form 941 Q1-Q4, Form 940 FUTA, W-2 furnish,
            W-2/W-3 SSA filing, 1099-NEC, 1099-MISC recipient, and 1099-MISC IRS filing.
          </Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 700, mb: 0.5 }}>
            Suppressed until profile facts are saved
          </Typography>
          <Typography sx={{ fontSize: "12px", mb: 1.25 }}>
            Form 990, 990-N, 8868, 990-PF, 990-T, Section 4960, for-profit
            1120/1120-S/1065/Schedule C, estimated tax, Form 945, ACA, Form 5500,
            Form 5500 IQPA audit lead time (large plans), PCORI, worker classification risk,
            501(r), 4958, state exemption renewal (non-WA), and Washington Charities Program
            renewal (WA nonprofits when flagged).
          </Typography>
          <Typography sx={{ fontSize: "12px", mb: 1.25 }}>
            Existing facilities that never save this section may under-show important Title 26
            obligations. Form 940 FUTA may also over-show for 501(c)(3) or government facilities
            while tax status is unknown.
          </Typography>
          <Typography sx={{ fontSize: "12px" }}>
            If fiscal year end is blank, kept tax items assume a calendar-year tax period ending
            12/31. That default does not make nonprofit or for-profit items appear by itself.
          </Typography>
        </Box>
      }
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "#0f172a",
            borderRadius: 2,
            boxShadow: "0 10px 30px rgba(15, 23, 42, 0.22)",
          },
        },
        arrow: { sx: { color: "#0f172a" } },
      }}
    >
      <InfoOutlinedIcon
        sx={{
          fontSize: 16,
          color: "#0e7490",
          cursor: "help",
          ml: 0.25,
        }}
      />
    </Tooltip>
  );
}

/** Activities master switch for Section 1557 (legacy workforce toggle deprecated). */
function is1557ActivityField(field: ProfileFieldResponse): boolean {
  return field.field_key === "receives_federal_financial_assistance";
}

/**
 * Returns true when an employee-count option label represents more than 14
 * employees. Parses all integers from the label and checks whether the
 * maximum exceeds 14. Works for labels like "1 – 14", "15 – 49", "100+", etc.
 */
function employeeCountOptionOver14(optionLabel: string): boolean {
  const nums = optionLabel.match(/\d+/g)?.map(Number) ?? [];
  if (nums.length === 0) return false;
  return Math.max(...nums) > 14;
}

/** True when a required field has no usable value (wizard / save validation). */
function fieldValueMissing(field: ProfileFieldResponse, val: unknown): boolean {
  if (val === null || val === undefined) return true;
  const t = field.hub_renderer_key || field.field_type;
  if (typeof val === "string" && val.trim() === "" && t !== "toggle" && t !== "checkbox") {
    return true;
  }
  if (
    (t === "checkbox_group" || t === "multiselect" || t === "accrediting_bodies") &&
    Array.isArray(val) &&
    val.length === 0
  ) {
    return true;
  }
  return false;
}

function fieldHasValidationError(
  field: ProfileFieldResponse,
  val: unknown,
  showErrors: boolean,
  trackedAnchorsRaw: unknown,
  ccnContext: CcnValidationContext = {},
): boolean {
  if (!showErrors) return false;
  if (!fieldVisibleForTrackedAnchors(field, trackedAnchorsRaw)) return false;
  if (isEmployeeCountField(field) && fieldValueMissing(field, val)) return true;
  if (field.is_required && field.visible !== false && fieldValueMissing(field, val)) {
    return true;
  }
  // SSOT-39 / AB#10163: CCN format, state prefix, and RHC type range
  if (isCcnProfileField(field) && validateCcnProfileValue(val, ccnContext)) {
    return true;
  }
  return false;
}

/**
 * Apply pre-population defaults to initial field values:
 * - Emergency Preparedness Plan Review: defaults to true when the field has no
 *   saved value.
 * - Employee count defaults to the first configured option, currently "1 - 14",
 *   when the field has no saved value.
 * - State license renewal cycle / lead days / anchor: common regulatory baseline
 *   when the profile has no saved value (1 year, 30 days before expiration,
 *   anniversary of license expiration / issuance).
 */
function applyDefaultValues(
  sections: ProfileSectionResponse[],
  values: Record<string, unknown>,
  jurisdictionContext?: FacilityProfileJurisdictionContext | null
): Record<string, unknown> {
  const result = { ...values };
  for (const section of sections) {
    for (const field of section.fields) {
      if (isEmployeeCountField(field) && fieldValueMissing(field, result[field.field_key])) {
        const firstOption = field.options?.find((option) => option.key);
        if (firstOption) {
          result[field.field_key] = firstOption.key;
        }
      }
    }
  }
  const withJurisdictionDefaults = applyStateLicenseRenewalDefaults(
    sections,
    result,
    jurisdictionContext,
  );
  return applyLiberalActivityToggleDefaults(sections, withJurisdictionDefaults);
}

function allRequiredFieldsPresent(
  sections: ProfileSectionResponse[],
  values: Record<string, unknown>
): boolean {
  const tracked = values["tracked_anchors"];
  for (const section of sections) {
    for (const field of section.fields) {
      if (field.visible === false) continue;
      if (!fieldVisibleForTrackedAnchors(field, tracked)) continue;
      const val = values[field.field_key];
      if (isEmployeeCountField(field) && fieldValueMissing(field, val)) return false;
      if (field.is_required && fieldValueMissing(field, val)) return false;
      if (isCcnProfileField(field) && validateCcnProfileValue(val)) return false;
    }
  }
  return true;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FacilityProfileTab: React.FC<FacilityProfileTabProps> = ({
  facilityId,
  facilityTypeSlug,
  autoSaveOnInitialLoad = false,
  onComplete,
  onPreferencePatch,
  deferProfileFetchUntilTabVisible = false,
  isProfileTabActive = true,
  profileSchemaWarmedFromBff = false,
  deferAutoSaveUntilComplianceInit = false,
  complianceStreamInitSignal = 0,
  skipComplianceCacheInvalidationOnAutoSave = false,
  readOnly = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [schema, setSchema] = useState<FacilityProfileSchemaResponse | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, unknown>>({});
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [medicarePrefillNote, setMedicarePrefillNote] = useState<string | null>(null);
  /** SSOT-60: saved profile cert date disagrees with CMS POS. */
  const [medicareCertConflict, setMedicareCertConflict] = useState<{
    profileDate: string;
    cmsDate: string;
  } | null>(null);

  const cancelledRef = useRef(false);
  // React 18 StrictMode runs effects twice in dev; guard to avoid duplicate calls.
  const fetchKeyRef = useRef<number | null>(null);
  /** After a successful save, skip redundant profile-schema GET when parent props churn. */
  const skipSchemaReloadAfterSaveRef = useRef<number | null>(null);
  const medicareAutoPrefillRunKeyRef = useRef<string | null>(null);
  const autoSaveAttemptKeyRef = useRef<number | null>(null);
  const autoSaveOnInitialLoadRef = useRef(autoSaveOnInitialLoad);
  const onCompleteRef = useRef(onComplete);
  const deferAutoSaveRef = useRef(deferAutoSaveUntilComplianceInit);
  const complianceInitSignalRef = useRef(complianceStreamInitSignal);
  const skipInvalidateOnAutoSaveRef = useRef(skipComplianceCacheInvalidationOnAutoSave);
  const profileSchemaWarmedRef = useRef(profileSchemaWarmedFromBff);
  const deferTimedOutRef = useRef(false);
  const pendingAutoSaveRef = useRef<{
    facilityId: number;
    initialValues: Record<string, unknown>;
  } | null>(null);
  const taxCalendarSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dateCommitRef = useRef<ProfileDateCommitApi | null>(null);
  // Kept in sync with `schema` state so handleFieldChange can read it without
  // needing schema in its useCallback dependency array.
  const schemaRef = useRef<FacilityProfileSchemaResponse | null>(null);
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  const fieldsDisabled = saving || readOnly;

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    medicareAutoPrefillRunKeyRef.current = null;
  }, [facilityId]);

  useEffect(() => {
    autoSaveOnInitialLoadRef.current = autoSaveOnInitialLoad;
  }, [autoSaveOnInitialLoad]);

  useEffect(() => {
    deferAutoSaveRef.current = deferAutoSaveUntilComplianceInit;
  }, [deferAutoSaveUntilComplianceInit]);

  useEffect(() => {
    complianceInitSignalRef.current = complianceStreamInitSignal;
  }, [complianceStreamInitSignal]);

  useEffect(() => {
    skipInvalidateOnAutoSaveRef.current = skipComplianceCacheInvalidationOnAutoSave;
  }, [skipComplianceCacheInvalidationOnAutoSave]);

  useEffect(() => {
    profileSchemaWarmedRef.current = profileSchemaWarmedFromBff;
  }, [profileSchemaWarmedFromBff]);

  useEffect(() => {
    deferTimedOutRef.current = false;
    pendingAutoSaveRef.current = null;
  }, [facilityId]);

  useEffect(
    () => () => {
      if (taxCalendarSaveTimerRef.current) {
        clearTimeout(taxCalendarSaveTimerRef.current);
        taxCalendarSaveTimerRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (isProfileTabActive && deferProfileFetchUntilTabVisible) {
      skipSchemaReloadAfterSaveRef.current = null;
    }
  }, [isProfileTabActive, deferProfileFetchUntilTabVisible]);

  const runInitialAutoSave = useCallback(
    async (initialValues: Record<string, unknown>) => {
      if (readOnlyRef.current) {
        return;
      }
      if (autoSaveAttemptKeyRef.current === facilityId) {
        return;
      }
      autoSaveAttemptKeyRef.current = facilityId;
      setSaving(true);
      try {
        const values = Object.entries(initialValues).map(([field_key, value]) => ({
          field_key,
          value,
        }));

        await saveFacilityProfileValues(facilityId, {
          values,
          approve_profile: true,
          ...(skipInvalidateOnAutoSaveRef.current
            ? { skip_compliance_cache_invalidation: true }
            : {}),
        });

        skipSchemaReloadAfterSaveRef.current = facilityId;
        onCompleteRef.current(initialValues);
      } catch (err) {
        if (!cancelledRef.current) {
          setError(mapSaveFacilityProfileError(err));
        }
      } finally {
        if (!cancelledRef.current) setSaving(false);
      }
    },
    [facilityId],
  );

  useEffect(() => {
    if (complianceStreamInitSignal <= 0) {
      return;
    }
    const pending = pendingAutoSaveRef.current;
    if (!pending || pending.facilityId !== facilityId) {
      return;
    }
    pendingAutoSaveRef.current = null;
    void runInitialAutoSave(pending.initialValues);
  }, [complianceStreamInitSignal, facilityId, runInitialAutoSave]);

  useEffect(() => {
    if (!deferAutoSaveUntilComplianceInit) {
      return;
    }
    if (complianceStreamInitSignal > 0) {
      return;
    }
    const timer = setTimeout(() => {
      deferTimedOutRef.current = true;
      const pending = pendingAutoSaveRef.current;
      if (!pending || pending.facilityId !== facilityId) {
        return;
      }
      pendingAutoSaveRef.current = null;
      void runInitialAutoSave(pending.initialValues);
    }, PROFILE_AUTO_SAVE_DEFER_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [
    deferAutoSaveUntilComplianceInit,
    complianceStreamInitSignal,
    facilityId,
    runInitialAutoSave,
  ]);

  const load = useCallback(async () => {
    if (deferProfileFetchUntilTabVisible && !isProfileTabActive) {
      setLoading(false);
      return;
    }
    if (
      skipSchemaReloadAfterSaveRef.current === facilityId &&
      schemaRef.current
    ) {
      setLoading(false);
      return;
    }
    // Bail out if we are already fetching for this facilityId (Strict Mode guard).
    if (fetchKeyRef.current === facilityId) return;
    fetchKeyRef.current = facilityId;

    cancelledRef.current = false;
    setMedicarePrefillNote(null);
    setLoading(true);
    setError(null);
    setSchema(null);
    setFieldValues({});
    try {
      const data = await getFacilityProfileSchema(facilityId, {
        skipExternalPrefill: profileSchemaWarmedRef.current,
      });
      if (cancelledRef.current) return;

      // If any field is an accrediting-body picker with no options, fetch the
      // available bodies from the compliance service and inject them.
      // Covers both the dedicated "accrediting_bodies" field_type and plain
      // "select" fields whose key contains "accredit" (the backend may emit
      // either depending on seed-data version).
      const needsBodyOptions = (f: ProfileFieldResponse) =>
        isAccreditingProfileField(f) && (!f.options || f.options.length === 0);

      const hasEmptyAccreditingBodiesField = data.sections.some((s) =>
        s.fields.some(needsBodyOptions),
      );

      let fetchedAccreditingBodyOptions: ProfileFieldOption[] | null = null;
      if (
        isProfileTabActive &&
        hasEmptyAccreditingBodiesField &&
        data.facility.facility_type_slug
      ) {
        try {
          const bodiesData = await facilityComplianceService.getAccreditingBodies({
            facilityType: data.facility.facility_type_slug,
            countryCode: data.facility.country_code || "USA",
          });
          if (!cancelledRef.current) {
            fetchedAccreditingBodyOptions = (bodiesData.bodies ?? []).map((body) => ({
              key: body.bodySlug,
              label: body.fullName || body.bodyAcronym || body.bodySlug,
            }));
          }
        } catch {
          // Non-fatal: the CMS fallback below still keeps the picker usable.
        }
      }

      // Accrediting body is a baseline profile fact. Always render the picker,
      // always include CMS / Federal Certification, and default to CMS when no
      // saved value exists.
      data.sections = data.sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) => {
          if (!isAccreditingProfileField(field)) return field;

          const existingOptions =
            field.options && field.options.length > 0
              ? field.options
              : fetchedAccreditingBodyOptions;
          const options = ensureCmsAccreditingBodyOption(existingOptions);
          const inner = unwrap(field.value as { value: unknown } | null);
          const hasExistingValue = accreditingSavedSelectionSlugs(inner).length > 0;

          const withOptions = { ...field, options, visible: true };
          if (hasExistingValue) return withOptions;

          const cmsOption = options.find(isCmsAccreditingBodyOption);
          return cmsOption
            ? {
                ...withOptions,
                value: { value: cmsDefaultAccreditingProfileValue(field, cmsOption) },
              }
            : withOptions;
        }),
      }));

      // Move accrediting-body fields to the top of section 0 (scroll layout only).
      // In wizard + tabs mode, that would strip them from cert and show them only on
      // Workforce; keep API section placement when wizard UI is active.
      const isWizardUi =
        data.ui?.profile_mode === "wizard" || data.ui?.show_tab_strip === true;
      if (!isWizardUi) {
        const liftedFields: ProfileFieldResponse[] = [];
        data.sections = data.sections.map((section) => {
          const accFields = section.fields.filter(isAccreditingProfileField);
          const rest = section.fields.filter((f) => !isAccreditingProfileField(f));
          liftedFields.push(...accFields);
          return { ...section, fields: rest };
        });

        if (liftedFields.length > 0 && data.sections.length > 0) {
          data.sections[0] = {
            ...data.sections[0],
            fields: [...liftedFields, ...data.sections[0].fields],
          };
        }
      }

      // Group Medicare-related fields together within each section so they
      // appear consecutively (Medicare cert date, CCN, revalidation date, etc.)
      // before other fields, preserving relative order within each group.
      const isMedicareField = (f: ProfileFieldResponse) => {
        const k = f.field_key.toLowerCase();
        return (
          k.includes("medicare") ||
          k.includes("cms_certification") ||
          k.includes("revalidation")
        );
      };

      data.sections = data.sections.map((section) => {
        const medicareFields = section.fields.filter(isMedicareField);
        const otherFields = section.fields.filter((f) => !isMedicareField(f));
        if (medicareFields.length === 0) return section;
        return { ...section, fields: [...medicareFields, ...otherFields] };
      });

      // Employee count is always required — enforce regardless of API value.
      data.sections = data.sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) =>
          isEmployeeCountField(field) ? { ...field, is_required: true } : field
        ),
      }));

      const initialValues = applyDefaultValues(
        data.sections,
        buildInitialValues(data.sections),
        data.jurisdiction_context,
      );
      const entityTaxRaw = initialValues["entity_tax_status"];
      const providerBasedRaw = initialValues["provider_based_status"];
      const sectionsForPresence = data.sections.map((s) =>
        mergeProviderBasedSectionForDisplay(
          mergeTaxSectionForDisplay(s, entityTaxRaw),
          providerBasedRaw,
        ),
      );

      schemaRef.current = data;
      setSchema(data);
      setFieldValues(initialValues);
      const profileCcn = medicareCcnFromProfileValue(initialValues.ccn);
      if (profileCcn) {
        onPreferencePatch?.(facilityId, { medicare_ccn: profileCcn });
      }
      setActiveSectionIndex(0);
      setMedicareCertConflict(null);

      const medicareCertFk = resolveMedicareCertificationDateFieldKey(data.sections);
      // SSOT-60: schema may already flag cms_status=conflict with POS date in envelope.
      for (const section of data.sections) {
        for (const field of section.fields) {
          if (
            isMedicareCertificationDateField(field) &&
            field.cms_status === "conflict"
          ) {
            const env =
              field.value && typeof field.value === "object"
                ? (field.value as Record<string, unknown>)
                : null;
            const cmsDate =
              typeof env?.cms_pos_certification_date === "string"
                ? env.cms_pos_certification_date
                : null;
            const profileDate =
              typeof env?.value === "string"
                ? env.value
                : initialValues[field.field_key] != null
                  ? String(initialValues[field.field_key])
                  : null;
            if (cmsDate && profileDate) {
              setMedicareCertConflict({ profileDate, cmsDate });
            }
            break;
          }
        }
      }

      const ccnRaw = initialValues["ccn"];
      const ccn = typeof ccnRaw === "string" ? ccnRaw.trim() : String(ccnRaw ?? "").trim();
      const mcd = initialValues[medicareCertFk];
      const hasMcd = mcd != null && String(mcd).trim() !== "";
      const prefillKey = `${facilityId}|${ccn}|medicare_prefill`;
      if (ccn && medicareAutoPrefillRunKeyRef.current !== prefillKey) {
        medicareAutoPrefillRunKeyRef.current = prefillKey;
        void getMedicareCertPrefill(facilityId)
          .then((prefill: MedicareCertPrefillData) => {
            if (cancelledRef.current) return;
            if (
              prefill.certification_date_conflict &&
              prefill.profile_certification_date &&
              prefill.cms_pos_certification_date
            ) {
              setMedicareCertConflict({
                profileDate: prefill.profile_certification_date,
                cmsDate: prefill.cms_pos_certification_date,
              });
              setMedicarePrefillNote(
                buildMedicareManualRefreshUserSummary(
                  prefill,
                  prefill.lookup_diagnostics ?? null,
                ),
              );
              return;
            }
            // Only auto-fill when the cert field is empty — never overwrite a saved date.
            if (!hasMcd && prefill.original_certification_date) {
              setFieldValues((prev) => ({
                ...prev,
                [medicareCertFk]: prefill.original_certification_date,
                ...(prefill.next_certification_date
                  ? { medicare_revalidation_date: prefill.next_certification_date }
                  : {}),
              }));
              setMedicarePrefillNote("Auto-filled from CMS POS CLIA");
            }
          })
          .catch(() => {
            /* non-fatal — user can run manual lookup */
          });
      }

      if (
        autoSaveOnInitialLoadRef.current &&
        autoSaveAttemptKeyRef.current !== facilityId &&
        allRequiredFieldsPresent(sectionsForPresence, initialValues)
      ) {
        if (
          shouldDeferProfileAutoSave({
            enabled: deferAutoSaveRef.current,
            complianceStreamInitSignal: complianceInitSignalRef.current,
            deferTimedOut: deferTimedOutRef.current,
          })
        ) {
          pendingAutoSaveRef.current = { facilityId, initialValues };
        } else {
          void runInitialAutoSave(initialValues);
        }
      }
    } catch {
      if (!cancelledRef.current) {
        // Reset key so the user can retry after an error.
        fetchKeyRef.current = null;
        setError("Failed to load facility profile. Please try again.");
      }
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [facilityId, deferProfileFetchUntilTabVisible, isProfileTabActive, runInitialAutoSave]);

  useEffect(() => {
    void load();
    return () => {
      cancelledRef.current = true;
      // Reset key on cleanup so a facilityId change or remount fetches fresh data.
      fetchKeyRef.current = null;
    };
  }, [load, deferProfileFetchUntilTabVisible, isProfileTabActive]);

  // Keep schemaRef in sync so handleFieldChange always has the latest schema.
  useEffect(() => {
    schemaRef.current = schema;
  }, [schema]);

  const sortedSections = useMemo(() => {
    if (!schema?.sections?.length) return [];
    const entityTaxRaw = fieldValues["entity_tax_status"];
    const providerBasedRaw = fieldValues["provider_based_status"];
    const trackedRaw = fieldValues["tracked_anchors"];
    return [...schema.sections]
      .sort(compareProfileSectionsForDisplay)
      .map((sec) =>
        mergeProviderBasedSectionForDisplay(
          mergeTaxSectionForDisplay(sec, entityTaxRaw),
          providerBasedRaw,
        ),
      )
      .filter(
        (sec) =>
          filterFieldsForTrackedAnchors(sec.fields, trackedRaw, fieldValues).length > 0,
      );
  }, [schema, fieldValues]);

  const anchorPresentationCtx = useMemo(
    () => ({
      labAnchorActive: Boolean(fieldValues.lab_anchor_active),
      nonCmsAccreditor: nonCmsAccreditorSelected(
        accreditingSavedSelectionSlugs(fieldValues.accrediting_body),
      ),
    }),
    [fieldValues.lab_anchor_active, fieldValues.accrediting_body],
  );

  // API: facility_profile_group.ui_config — profile_mode "wizard" enables step-by-step layout.
  // show_tab_strip (e.g. aggregator migration 202604121301) turns on section tabs for all groups
  // without necessarily setting profile_mode; the hub must honor both flags.
  const isWizardMode =
    schema?.ui?.profile_mode === "wizard" || schema?.ui?.show_tab_strip === true;
  const showSectionTabs = schema?.ui?.show_tab_strip === true;

  const mergeFieldValuesForSave = useCallback(
    (values: Record<string, unknown>) =>
      dateCommitRef.current?.mergePendingDateValues(values) ?? values,
    [],
  );

  const ccnValidationContext = useMemo<CcnValidationContext>(
    () => ({
      state: schema?.facility?.state ?? schema?.jurisdiction_context?.state ?? null,
      facilityTypeSlug:
        facilityTypeSlug || schema?.facility?.facility_type_slug || null,
      providerBasedStatus:
        fieldValues["provider_based_status"] != null
          ? String(fieldValues["provider_based_status"])
          : null,
    }),
    [
      schema?.facility?.state,
      schema?.facility?.facility_type_slug,
      schema?.jurisdiction_context?.state,
      facilityTypeSlug,
      fieldValues,
    ],
  );

  const validateSectionFields = useCallback(
    (section: ProfileSectionResponse, valuesOverride?: Record<string, unknown>): boolean => {
      const source = valuesOverride ?? fieldValues;
      const tracked = source["tracked_anchors"];
      const ctx: CcnValidationContext = {
        ...ccnValidationContext,
        providerBasedStatus:
          source["provider_based_status"] != null
            ? String(source["provider_based_status"])
            : ccnValidationContext.providerBasedStatus,
      };
      for (const field of section.fields) {
        if (field.visible === false) continue;
        if (!fieldVisibleForTrackedAnchors(field, tracked)) continue;
        const val = source[field.field_key];
        if (isEmployeeCountField(field) && fieldValueMissing(field, val)) return false;
        if (field.is_required && fieldValueMissing(field, val)) return false;
        if (isCcnProfileField(field) && validateCcnProfileValue(val, ctx)) return false;
      }
      return true;
    },
    [fieldValues, ccnValidationContext],
  );

  const validateAllRequiredFields = useCallback(
    (valuesOverride?: Record<string, unknown>): boolean => {
      if (!schemaRef.current) return false;
      const source = valuesOverride ?? fieldValues;
      const taxRaw = source["entity_tax_status"];
      const providerBasedRaw = source["provider_based_status"];
      for (const section of schemaRef.current.sections) {
        const merged = mergeProviderBasedSectionForDisplay(
          mergeTaxSectionForDisplay(section, taxRaw),
          providerBasedRaw,
        );
        if (!validateSectionFields(merged, source)) return false;
      }
      return true;
    },
    [validateSectionFields, fieldValues],
  );

  const handleWizardNext = useCallback(() => {
    const sec = sortedSections[activeSectionIndex];
    if (!sec) return;
    const mergedValues = mergeFieldValuesForSave(fieldValues);
    if (!validateSectionFields(sec, mergedValues)) {
      setValidationError("Please complete all required fields on this step before continuing.");
      setShowFieldErrors(true);
      return;
    }
    setFieldValues(mergedValues);
    setValidationError(null);
    setShowFieldErrors(false);
    setActiveSectionIndex((i) => Math.min(i + 1, sortedSections.length - 1));
  }, [activeSectionIndex, sortedSections, validateSectionFields, mergeFieldValuesForSave, fieldValues]);

  const handleWizardBack = useCallback(() => {
    setActiveSectionIndex((i) => Math.max(0, i - 1));
    setValidationError(null);
    setShowFieldErrors(false);
  }, []);

  const handleFieldChange = useCallback(
    (fieldKey: string, value: unknown) => {
      if (readOnlyRef.current) {
        return;
      }
      if (fieldKey === "ccn") {
        const profileCcn = medicareCcnFromProfileValue(value);
        if (profileCcn) {
          onPreferencePatch?.(facilityId, { medicare_ccn: profileCcn });
        }
      }
      if (fieldKey === SHOW_TAX_COMPLIANCE_CALENDAR_FIELD_KEY) {
        const nextBool = Boolean(value);
        onPreferencePatch?.(facilityId, {
          [SHOW_TAX_COMPLIANCE_CALENDAR_FIELD_KEY]: nextBool,
        });
        if (taxCalendarSaveTimerRef.current) {
          clearTimeout(taxCalendarSaveTimerRef.current);
        }
        taxCalendarSaveTimerRef.current = setTimeout(() => {
          taxCalendarSaveTimerRef.current = null;
          void saveFacilityProfileValues(facilityId, {
            values: [
              { field_key: SHOW_TAX_COMPLIANCE_CALENDAR_FIELD_KEY, value: nextBool },
            ],
            approve_profile: true,
          }).catch(() => {
            /* non-fatal — user can use Save Profile */
          });
        }, 600);
      }

      setFieldValues((prev) => {
        const updates: Record<string, unknown> = { ...prev, [fieldKey]: value };

        // Auto-enable Section 1557 toggle when employee count > 14.
        const currentSchema = schemaRef.current;
        if (currentSchema && typeof value === "string") {
          const allFields = currentSchema.sections.flatMap((s) => s.fields);
          const changedField = allFields.find((f) => f.field_key === fieldKey);

          if (changedField && isEmployeeCountField(changedField)) {
            // Clear validation error once the user picks a value.
            if (value) {
              setValidationError(null);
              setShowFieldErrors(false);
            }

            const opt = (changedField.options ?? []).find((o) => o.key === value);
            if (opt && employeeCountOptionOver14(opt.label)) {
              const field1557 = allFields.find(is1557ActivityField);
              if (field1557) {
                updates[field1557.field_key] = true;
              }
            }
          }
        }

        return updates;
      });
    },
    [facilityId, onPreferencePatch],
  );

  const handleAcceptAndContinue = async () => {
    if (readOnly) {
      return;
    }
    setError(null);
    setValidationError(null);

    const mergedValues = mergeFieldValuesForSave(fieldValues);

    if (schemaRef.current) {
      if (!validateAllRequiredFields(mergedValues)) {
        setValidationError(
          "Please complete all required fields before saving.",
        );
        setShowFieldErrors(true);
        return;
      }
    }

    // SSOT-43 / #10167 — RHC/FQHC CLIA-off is a certification-condition risk.
    const resolvedSlug =
      facilityTypeSlug || schema?.facility?.facility_type_slug || null;
    if (shouldWarnCliaTestingOff(resolvedSlug, mergedValues)) {
      const proceed = window.confirm(CLIA_OFF_WARNING_RHC);
      if (!proceed) {
        setValidationError(CLIA_OFF_WARNING_RHC);
        return;
      }
    }

    setFieldValues(mergedValues);
    setSaving(true);
    try {
      const values = Object.entries(mergedValues).map(([field_key, value]) => ({
        field_key,
        value,
      }));

      await saveFacilityProfileValues(facilityId, {
        values,
        approve_profile: true,
      });

      toast.success("Profile saved");
      skipSchemaReloadAfterSaveRef.current = facilityId;
      onComplete(mergedValues);
    } catch (err) {
      setError(mapSaveFacilityProfileError(err));
    } finally {
      if (!cancelledRef.current) setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  /** Field types that need the full row width rather than half. */
  function isFullWidthField(field: ProfileFieldResponse): boolean {
    const t = field.hub_renderer_key || field.field_type;
    return ["textarea", "checkbox_group"].includes(t);
  }

  function renderProfileField(field: ProfileFieldResponse) {
    const presented = augmentFieldForAnchorPresentation(field, anchorPresentationCtx);
    const val = fieldValues[presented.field_key] ?? null;
    const trackedRaw = fieldValues["tracked_anchors"];
    const inner = (
      <ProfileField
        field={presented}
        value={val}
        onChange={handleFieldChange}
        disabled={fieldsDisabled}
        hasError={fieldHasValidationError(presented, val, showFieldErrors, trackedRaw, ccnValidationContext)}
      />
    );
    if (isCcnProfileField(presented)) {
      const ccnFieldKey = presented.field_key;
      return (
        <Box>
          {inner}
          <CcnCmsLookupButton
            facilityId={facilityId}
            disabled={fieldsDisabled}
            onResolved={(ccn) => {
              setFieldValues((prev) => ({ ...prev, [ccnFieldKey]: ccn }));
              const profileCcn = medicareCcnFromProfileValue(ccn);
              if (profileCcn) {
                onPreferencePatch?.(facilityId, { medicare_ccn: profileCcn });
              }
            }}
          />
        </Box>
      );
    }
    if (isMedicareCertificationDateField(presented)) {
      const medicareCertFieldKey = presented.field_key;
      const showConflict =
        presented.cms_status === "conflict" || medicareCertConflict != null;
      return (
        <Box>
          {inner}
          {showConflict && medicareCertConflict ? (
            <Alert
              severity="warning"
              sx={{ mt: 1, mb: 0.5, fontFamily: "Rethink Sans, sans-serif" }}
              action={
                <Button
                  type="button"
                  color="inherit"
                  size="small"
                  disabled={fieldsDisabled}
                  onClick={() => {
                    setFieldValues((prev) => ({
                      ...prev,
                      [medicareCertFieldKey]: medicareCertConflict.cmsDate,
                    }));
                    setMedicareCertConflict(null);
                    setMedicarePrefillNote(
                      `Applied CMS POS date ${medicareCertConflict.cmsDate}`,
                    );
                  }}
                  sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  Use CMS date
                </Button>
              }
            >
              Saved Medicare certification date ({medicareCertConflict.profileDate}){" "}
              disagrees with CMS Provider of Services ({medicareCertConflict.cmsDate}).
            </Alert>
          ) : null}
          {medicarePrefillNote && !showConflict ? (
            <Typography
              variant="caption"
              sx={{
                color: "#15803d",
                display: "block",
                mt: 0.75,
                fontFamily: "Rethink Sans, sans-serif",
              }}
            >
              {medicarePrefillNote}
            </Typography>
          ) : null}
          <MedicareCertLookupButton
            facilityId={facilityId}
            disabled={fieldsDisabled}
            onManualRefreshNote={setMedicarePrefillNote}
            onPrefill={(data) => {
              if (
                data.certification_date_conflict &&
                data.profile_certification_date &&
                data.cms_pos_certification_date
              ) {
                setMedicareCertConflict({
                  profileDate: data.profile_certification_date,
                  cmsDate: data.cms_pos_certification_date,
                });
                setMedicarePrefillNote(
                  buildMedicareManualRefreshUserSummary(
                    data,
                    data.lookup_diagnostics ?? null,
                  ),
                );
                // Keep saved profile date on the form; user can click Use CMS date.
                setFieldValues((prev) => {
                  const next = { ...prev };
                  next[medicareCertFieldKey] = data.profile_certification_date;
                  if (typeof data.is_dmepos_supplier === "boolean") {
                    next.is_dmepos = data.is_dmepos_supplier;
                  }
                  if (typeof data.lab_anchor_active === "boolean") {
                    next.lab_anchor_active = data.lab_anchor_active;
                  }
                  return next;
                });
                return;
              }
              setMedicareCertConflict(null);
              setFieldValues((prev) => {
                const next = { ...prev };
                if (data.original_certification_date) {
                  next[medicareCertFieldKey] = data.original_certification_date;
                }
                if (data.next_certification_date) {
                  next.medicare_revalidation_date = data.next_certification_date;
                }
                if (typeof data.is_dmepos_supplier === "boolean") {
                  next.is_dmepos = data.is_dmepos_supplier;
                }
                if (typeof data.lab_anchor_active === "boolean") {
                  next.lab_anchor_active = data.lab_anchor_active;
                }
                return next;
              });
            }}
          />
        </Box>
      );
    }
    return inner;
  }

  function renderLayoutFieldRows(fieldList: ProfileFieldResponse[]) {
    const rows = groupFieldsIntoRows(fieldList);
    /** Readable minimum so many API `layout_row` siblings do not crush into one unreadable strip. */
    const fieldCellMinPx = 360;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75, width: "100%" }}>
        {rows.map((rowFields, rowIdx) => (
          <Box
            key={`row-${rowIdx}`}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              columnGap: 2,
              rowGap: 2,
              width: "100%",
              alignItems: "stretch",
            }}
          >
            {rowFields.map((field) => {
              const full = isFullWidthField(field);
              const compactDate = isDateProfileField(field) && !full;
              const pct = field.layout_width_pct;
              return (
                <Box
                  key={field.field_key}
                  sx={{
                    boxSizing: "border-box",
                    ...(full
                      ? { flex: "1 1 100%", width: "100%", minWidth: 0 }
                      : compactDate
                        ? {
                            flex: `0 1 ${PROFILE_DATE_FIELD_MAX_WIDTH_PX}px`,
                            width: { xs: "100%", sm: `${PROFILE_DATE_FIELD_MAX_WIDTH_PX}px` },
                            minWidth: { xs: "100%", sm: `${PROFILE_DATE_FIELD_MAX_WIDTH_PX}px` },
                            maxWidth: "100%",
                          }
                        : pct != null
                          ? {
                              flex: `1 1 min(100%, max(${fieldCellMinPx}px, ${pct}%))`,
                              minWidth: { xs: "100%", sm: `${fieldCellMinPx}px` },
                              maxWidth: "100%",
                            }
                          : {
                              flex: `1 1 ${fieldCellMinPx}px`,
                              minWidth: { xs: "100%", sm: `${fieldCellMinPx}px` },
                              maxWidth: "100%",
                            }),
                  }}
                >
                  {renderProfileField(field)}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    );
  }

  function renderRegionBlock(region: ProfileRegionResponse, fieldsInRegion: ProfileFieldResponse[]) {
    if (fieldsInRegion.length === 0) return null;
    const dynamicIdentifiersSubtitle =
      region.region_key === "cert_identifiers"
        ? buildCertIdentifiersRegionSubtitle(fieldsInRegion)
        : null;
    const regionSubtitle =
      region.region_key === "cert_identifiers" && dynamicIdentifiersSubtitle
        ? dynamicIdentifiersSubtitle
        : region.subtitle;

    return (
      <Box key={region.id} sx={regionStyleSx(region.style ?? null)}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontFamily: "Rethink Sans, sans-serif",
            fontSize: "12px",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            mb: regionSubtitle ? 0.5 : 1,
          }}
        >
          {region.label}
        </Typography>
        {regionSubtitle && (
          <Typography
            variant="caption"
            sx={{ color: "#6b7280", display: "block", mb: 1.5, fontFamily: "Rethink Sans, sans-serif" }}
          >
            {regionSubtitle}
          </Typography>
        )}
        {regionUsesTwoColumnDateGrid(region, fieldsInRegion) ? (
          <Box
            sx={{
              display: "grid",
              width: "100%",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fill, minmax(min(100%, 360px), 1fr))",
              },
              columnGap: 2,
              rowGap: 2,
              alignItems: "stretch",
            }}
          >
            {fieldsInRegion.map((field) => (
              <Box key={field.field_key} sx={{ minWidth: 0, width: "100%" }}>
                {renderProfileField(field)}
              </Box>
            ))}
          </Box>
        ) : (
          renderLayoutFieldRows(fieldsInRegion)
        )}
      </Box>
    );
  }

  function renderSection(section: ProfileSectionResponse) {
    const trackedRaw = fieldValues["tracked_anchors"];
    const visibleFields = filterFieldsForTrackedAnchors(
      section.fields,
      trackedRaw,
      fieldValues,
    );
    if (visibleFields.length === 0) return null;
    /** Schema may omit this key until Alembic `20260506_show_tax_cal` is applied — we still render a Hub toggle and filter duplicates. */
    const layoutFields =
      section.section_key === "tax"
        ? visibleFields.filter((f) => f.field_key !== SHOW_TAX_COMPLIANCE_CALENDAR_FIELD_KEY)
        : visibleFields;

    const sectionBoxMb = isWizardMode ? 0 : 4;
    const sectionDividerSx = isWizardMode
      ? { mt: 1, mb: 0, borderColor: "#f3f4f6" }
      : { mt: 2, mb: 1, borderColor: "#f3f4f6" };

    const header = (
      <>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
          }}
        >
          {section.color && (
            <Box
              sx={{
                width: 4,
                height: 18,
                borderRadius: 1,
                backgroundColor: section.color,
                flexShrink: 0,
              }}
            />
          )}
          <Typography
            variant="subtitle2"
            sx={{
              color: section.color ?? "#1f2937",
              fontWeight: 600,
              fontFamily: "Rethink Sans, sans-serif",
              fontSize: "13px",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {section.label}
          </Typography>
          {section.section_key === "tax" && <TaxCalendarImpactTooltip />}
        </Box>
        {section.description && (
          <Typography
            variant="body2"
            sx={{ color: "#6b7280", mb: 2, fontFamily: "Rethink Sans, sans-serif" }}
          >
            {section.description}
          </Typography>
        )}
        {section.section_key === ACTIVITIES_SECTION_KEY && (
          <Typography
            variant="body2"
            sx={{ color: "#6b7280", mb: 2, fontFamily: "Rethink Sans, sans-serif" }}
          >
            {ACTIVITIES_SECTION_HELPER_TEXT}
          </Typography>
        )}
      </>
    );

    const taxCalendarPreferenceRow =
      section.section_key === "tax" ? (
        <Box sx={{ mb: 2 }}>
          <Box sx={PROFILE_TOGGLE_ROW_SX}>
            <Switch
              checked={Boolean(fieldValues[SHOW_TAX_COMPLIANCE_CALENDAR_FIELD_KEY])}
              onChange={(_, checked) =>
                handleFieldChange(SHOW_TAX_COMPLIANCE_CALENDAR_FIELD_KEY, checked)
              }
              disabled={fieldsDisabled}
              sx={PROFILE_TEAL_SWITCH_SX}
            />
            <Box
              component="span"
              sx={{
                ...PROFILE_TOGGLE_TEXT_SX,
                fontSize: "15px",
                fontWeight: 600,
                color: "#1f2937",
              }}
            >
              Show Tax Compliance Calendar
            </Box>
          </Box>
        </Box>
      ) : null;

    if (!sectionUsesAdvancedLayout(section)) {
      return (
        <Box key={section.section_key} sx={{ mb: sectionBoxMb }}>
          {header}
          {taxCalendarPreferenceRow}
          <Box
            sx={{
              display: "grid",
              width: "100%",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fill, minmax(min(100%, 360px), 1fr))",
              },
              columnGap: 3,
              rowGap: 2,
            }}
          >
            {layoutFields.map((field: ProfileFieldResponse) => (
              <Box
                key={field.field_key}
                sx={{
                  gridColumn: isFullWidthField(field) ? "1 / -1" : "auto",
                  minWidth: 0,
                  width: "100%",
                }}
              >
                {renderProfileField(field)}
              </Box>
            ))}
          </Box>
          <Divider sx={sectionDividerSx} />
        </Box>
      );
    }

    const rootFields = layoutFields.filter(
      (f) => !f.region_key || String(f.region_key).length === 0,
    );
    const sortedRegions = [...(section.regions ?? [])].sort(
      (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
    );

    return (
      <Box key={section.section_key} sx={{ mb: sectionBoxMb }}>
        {header}
        {taxCalendarPreferenceRow}
        {rootFields.length > 0 ? renderLayoutFieldRows(rootFields) : null}
        {sortedRegions.map((region) => {
          const inRegion = layoutFields.filter((f) => f.region_key === region.region_key);
          return renderRegionBlock(region, inRegion);
        })}
        <Divider sx={sectionDividerSx} />
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Full render
  // ---------------------------------------------------------------------------

  const facilityLabel =
    schema?.facility.facility_type_display_name ??
    schema?.facility.profile_group_label ??
    "Your Facility";

  return (
    <ProfileDateCommitProvider apiRef={dateCommitRef}>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0, width: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "8px 0",
          borderBottom: "1px solid #e5e7eb",
          marginBottom: "4px",
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 3a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm-1 5h2v7h-2v-7z"
              fill="#065B70"
            />
          </svg>
          <Typography
            sx={{
              color: "#065B70",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "normal",
              fontFamily: "Rethink Sans, sans-serif",
            }}
          >
            Facility Profile
          </Typography>
        </Box>
      </Box>

      {/* Body — full width of parent (matches facility header row; avoid narrow centered column) */}
      <Box sx={{ width: "100%", maxWidth: "100%", mt: 3, px: 0, boxSizing: "border-box" }}>
        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 4 }}>
            <CircularProgress size={24} sx={{ color: "#0e7490" }} />
            <Typography variant="body2" sx={{ color: "#6b7280" }}>
              Loading facility profile data…
            </Typography>
          </Box>
        )}

        {!loading && (
          <>
            <Typography
              variant="body2"
              sx={{
                color: "#4b5563",
                fontFamily: "Rethink Sans, sans-serif",
                mb: 4,
              }}
            >
              The fields below have been populated based on available data for
              your <strong>{facilityLabel}</strong> facility. Review and update where
              needed, then save your profile. Compliance analysis runs on the Dashboard.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            {validationError && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                {validationError}
              </Alert>
            )}

            {schema && sortedSections.length > 0 ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid #e5e7eb",
                  bgcolor: "#fff",
                  p: { xs: 2, sm: 3 },
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
                  mt: 1,
                }}
              >
                {isWizardMode && showSectionTabs && sortedSections.length > 0 && (
                  <Tabs
                    value={activeSectionIndex}
                    onChange={(_, v) => {
                      setActiveSectionIndex(v);
                      setValidationError(null);
                      setShowFieldErrors(false);
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      mb: 2,
                      "& .MuiTabs-flexContainer": {
                        justifyContent: "flex-start",
                      },
                      /* Scrollable tabs keep a 40px-wide left button while disabled (opacity only),
                         which misaligns the first tab vs form content below. Collapse when not needed. */
                      "& .MuiTabScrollButton-root.Mui-disabled": {
                        width: 0,
                        minWidth: 0,
                        maxWidth: 0,
                        overflow: "hidden",
                        opacity: 0,
                        pointerEvents: "none",
                      },
                      "& .MuiTab-root": {
                        textTransform: "none",
                        fontWeight: 600,
                        fontFamily: "Rethink Sans, sans-serif",
                      },
                      "& .Mui-selected": { color: "#0e7490" },
                      "& .MuiTabs-indicator": { backgroundColor: "#0e7490" },
                    }}
                  >
                    {sortedSections.map((s, idx) => (
                      <Tab key={s.section_key} label={s.label} id={`facility-profile-tab-${idx}`} />
                    ))}
                  </Tabs>
                )}

                {isWizardMode
                  ? sortedSections[activeSectionIndex]
                    ? renderSection(sortedSections[activeSectionIndex])
                    : null
                  : sortedSections.map(renderSection)}

                {isWizardMode && sortedSections.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                      mt: 0.5,
                      pt: 1,
                      borderTop: "1px solid #f3f4f6",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#6b7280", fontFamily: "Rethink Sans, sans-serif" }}
                    >
                      {activeSectionIndex + 1} of {sortedSections.length}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="outlined"
                        onClick={handleWizardBack}
                        disabled={saving || loading || activeSectionIndex === 0}
                        sx={{ textTransform: "none", borderColor: "#0e7490", color: "#0e7490" }}
                      >
                        Back
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleWizardNext}
                        disabled={saving || loading || activeSectionIndex >= sortedSections.length - 1}
                        sx={{ textTransform: "none", borderColor: "#0e7490", color: "#0e7490" }}
                      >
                        Next
                      </Button>
                    </Box>
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: { xs: "stretch", sm: "flex-end" },
                    gap: 1,
                    mt: 1,
                    pt: 2,
                    borderTop: "1px solid #e5e7eb",
                  }}
                >
                  <Box sx={{ textAlign: { xs: "left", sm: "right" }, width: "100%" }}>
                    <Button
                      variant="contained"
                      onClick={handleAcceptAndContinue}
                      disabled={fieldsDisabled || loading}
                      sx={{
                        backgroundColor: "#0e7490",
                        "&:hover": { backgroundColor: "#0b5f75" },
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: "none",
                        fontFamily: "Rethink Sans, sans-serif",
                        fontSize: "15px",
                      }}
                    >
                      {saving ? (
                        <CircularProgress size={18} sx={{ color: "#fff" }} />
                      ) : (
                        "Save Profile"
                      )}
                    </Button>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 1.5,
                        color: "#6b7280",
                        fontFamily: "Rethink Sans, sans-serif",
                        fontSize: "12px",
                        fontStyle: "italic",
                      }}
                    >
                      Your profile saves quickly. View updated compliance analysis on
                      the Dashboard after saving.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ) : (
              !error && (
                <Typography variant="body2" sx={{ color: "#6b7280", py: 2 }}>
                  No profile fields are defined for this facility type yet.
                </Typography>
              )
            )}
          </>
        )}
      </Box>

    </Box>
    </ProfileDateCommitProvider>
  );
};

export default FacilityProfileTab;
